import { useEffect, useMemo, useRef, useState } from 'react';
import routingService from '@/pages/Business/pages/Shippings/services/routing.service';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import type { RouteMonitoring } from '../models';

/**
 * Fetches the real per-shipping address data (origin/destination + geo) for all
 * shippings in a route. The route detail endpoint only returns a single
 * `visit.address` (the commerce address for pickups), so we enrich each shipping
 * by number to be able to show the correct pickup (origin) address.
 *
 * Uses `GET /shipping/{number}` (routing shape) which works for any shipping
 * status and returns `originAddress`, `destinationAddress`, `visitType` and
 * `serviceVisitTypeName`.
 */
export function useRouteVisitShippingEnrichment(
  routeMonitoring: RouteMonitoring | undefined
): { byNumber: Map<number, ShippingForRouting>; pendingCount: number } {
  const [fetchByNumber] =
    routingService.useLazyGetShippingByNumberForRoutingQuery();

  const [byNumber, setByNumber] = useState<Map<number, ShippingForRouting>>(
    new Map()
  );
  const [failedNumbers, setFailedNumbers] = useState<Set<number>>(new Set());

  const logisticOperatorId = routeMonitoring?.route.logisticOperatorId ?? '';

  const shippingNumbers = useMemo(() => {
    if (!routeMonitoring) return [] as number[];
    const numbers = new Set<number>();
    routeMonitoring.visits.forEach((visit) => {
      (visit.shippings ?? []).forEach((shipping) => {
        if (shipping?.number != null && shipping.number > 0) {
          numbers.add(shipping.number);
        }
      });
      if (visit.shippingNumber > 0) {
        numbers.add(visit.shippingNumber);
      }
    });
    return [...numbers];
  }, [routeMonitoring]);

  const requestedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    requestedRef.current = new Set();
    setByNumber(new Map());
    setFailedNumbers(new Set());
  }, [logisticOperatorId]);

  useEffect(() => {
    if (shippingNumbers.length === 0) return;

    const toFetch = shippingNumbers.filter(
      (number) => !requestedRef.current.has(`${logisticOperatorId}:${number}`)
    );
    if (toFetch.length === 0) return;

    toFetch.forEach((number) =>
      requestedRef.current.add(`${logisticOperatorId}:${number}`)
    );

    let cancelled = false;
    let settled = false;

    void Promise.all(
      toFetch.map(async (number) => {
        try {
          const result = await fetchByNumber({
            number,
            logisticOperatorId,
          }).unwrap();
          return { number, shipping: result ?? null };
        } catch {
          return { number, shipping: null };
        }
      })
    ).then((entries) => {
      settled = true;
      if (cancelled) return;
      const valid = entries.filter(
        (entry): entry is { number: number; shipping: ShippingForRouting } =>
          entry.shipping !== null
      );
      const failed = entries.filter((entry) => entry.shipping === null);
      if (valid.length > 0) {
        setByNumber((prev) => {
          const next = new Map(prev);
          valid.forEach(({ number, shipping }) => next.set(number, shipping));
          return next;
        });
      }
      if (failed.length > 0) {
        setFailedNumbers((prev) => {
          const next = new Set(prev);
          failed.forEach(({ number }) => next.add(number));
          return next;
        });
      }
    });

    return () => {
      cancelled = true;
      if (!settled) {
        toFetch.forEach((number) =>
          requestedRef.current.delete(`${logisticOperatorId}:${number}`)
        );
      }
    };
  }, [shippingNumbers, logisticOperatorId, fetchByNumber]);

  const pendingCount = useMemo(
    () =>
      shippingNumbers.filter(
        (number) => !byNumber.has(number) && !failedNumbers.has(number)
      ).length,
    [shippingNumbers, byNumber, failedNumbers]
  );

  return { byNumber, pendingCount };
}

export function useRouteVisitShippingAddresses(
  routeMonitoring: RouteMonitoring | undefined
): Map<number, ShippingForRouting> {
  return useRouteVisitShippingEnrichment(routeMonitoring).byNumber;
}

export default useRouteVisitShippingAddresses;
